import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { ResponsiveImage } from '../../../components';
import { MAX_LISTING_IMAGES } from '../../../config/configLayout';

import css from './ListingImageGridMobile.module.css';

/**
 * Shows the first listing photos on a phone-sized screen in one block that
 * always fits on the screen (no scrolling inside the block).
 *
 * - 1 photo: the photo fills the whole width.
 * - 2 to 4 photos: the first photo sits on the left and is smaller than before,
 *   and the other photos are stacked next to it on the right.
 *
 * Photos after `maxImages` are not shown here. They are still reachable through
 * the full-screen carousel.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<Object>} props.images - The listing images
 * @param {Array<string>} props.imageVariants - The image variant names given to ResponsiveImage
 * @param {number} [props.maxImages] - How many photos to show at most
 * @param {Function} [props.onImageClick] - Called with the index of the photo that was tapped and the click event
 * @param {React.ReactNode} [props.children] - Extra content drawn on top of the first photo
 * @returns {JSX.Element|null} the grid, or null when there are no photos
 */
const ListingImageGridMobile = props => {
  const {
    rootClassName,
    className,
    images = [],
    imageVariants,
    maxImages = MAX_LISTING_IMAGES,
    onImageClick,
    children,
  } = props;
  const intl = useIntl();

  const visibleImages = images.slice(0, maxImages);
  if (visibleImages.length === 0) {
    return null;
  }

  const [firstImage, ...restImages] = visibleImages;
  const hasSideImages = restImages.length > 0;

  const renderTile = (image, index, tileClassName, sizes) => (
    <button
      key={image?.id?.uuid || `listing-image-${index}`}
      type="button"
      className={classNames(css.tile, tileClassName)}
      onClick={onImageClick ? e => onImageClick(index, e) : null}
      data-testid={index === 0 ? 'mobile-grid-main-image' : 'mobile-grid-side-image'}
    >
      <ResponsiveImage
        rootClassName={css.image}
        alt={intl.formatMessage(
          { id: 'ListingImageGridMobile.imageAltText' },
          { index: index + 1, count: visibleImages.length }
        )}
        image={image}
        variants={imageVariants}
        sizes={sizes}
      />
    </button>
  );

  const classes = classNames(rootClassName || css.root, className, {
    [css.singleImage]: !hasSideImages,
  });

  return (
    <div className={classes} data-testid="mobile-image-grid">
      {/* The overlay is a sibling of the button, not a child of it, because a
          button is not allowed to contain other clickable elements. */}
      <div className={css.mainTileWrapper}>
        {renderTile(firstImage, 0, css.mainTile, hasSideImages ? '62vw' : '100vw')}
        {children}
      </div>
      {hasSideImages ? (
        <div className={css.sideColumn}>
          {restImages.map((image, i) => renderTile(image, i + 1, css.sideTile, '38vw'))}
        </div>
      ) : null}
    </div>
  );
};

export default ListingImageGridMobile;
